import type { SubscriptionTier } from "@/types/projects";

export const storyImportFormats = ["docx", "pdf", "txt", "md"] as const;
export type StoryImportFormat = (typeof storyImportFormats)[number];

export const storyImportConfidenceLevels = ["high", "medium", "low"] as const;
export type StoryImportConfidence = (typeof storyImportConfidenceLevels)[number];

export interface StoryImportConfidenceDetails {
  band: StoryImportConfidence;
  citationStrength: number;
  contradictionPenalty: number;
  crossChunkSupport: number;
  modelConfidence: number;
  resolutionScore: number;
  schemaCompleteness: number;
  score: number;
}

export const storyImportReviewStatuses = [
  "pending",
  "accepted",
  "discarded",
  "merged",
  "linked",
] as const;
export type StoryImportReviewStatus = (typeof storyImportReviewStatuses)[number];

export const storyImportEditorTargets = [
  "story",
  "entity",
  "lore",
  "timeline",
  "governance",
] as const;
export type StoryImportEditorTarget = (typeof storyImportEditorTargets)[number];

export const storyImportReviewElementTypes = [
  "chapter",
  "character",
  "item",
  "location",
  "world_system",
  "governance",
  "story_spine",
  "timeline_event",
  "relationship",
] as const;
export type StoryImportReviewElementType = (typeof storyImportReviewElementTypes)[number];

export type StoryImportBulkAcceptElementType = Exclude<
  StoryImportReviewElementType,
  "relationship"
>;

export const storyImportSteps = [
  "upload",
  "credit-estimate",
  "processing",
] as const;
export type StoryImportStep = (typeof storyImportSteps)[number];

export type StoryImportJobStatus =
  | "draft"
  | "queued"
  | "pending"
  | "processing"
  | "canceling"
  | "canceled"
  | "review_ready"
  | "importing"
  | "complete"
  | "completed"
  | "failed";

export type StoryImportErrorCode =
  | "generic"
  | "missing-config"
  | "access-denied"
  | "tier-locked"
  | "insufficient-credits"
  | "invalid-file"
  | "file-too-large"
  | "file-too-many-pages"
  | "review-not-ready"
  | "source-not-found"
  | "text-extraction-failed"
  | "upload-failed"
  | "start-failed"
  | "already-running"
  | "retry-not-allowed"
  | "cancel-not-allowed"
  | "review-action-failed"
  | "review-element-not-found"
  | "review-element-resolved";

export interface StoryImportDraft {
  detectedFormat: StoryImportFormat;
  file: File;
  fileName: string;
  fileSizeBytes: number;
  mimeType: string;
  projectId: string | null;
  sessionId: string;
  storagePath: string | null;
}

export interface StoryImportCreditQuote {
  creditCost: number;
  currentBalance: number;
  estimatedAnalysisTokenCount: number | null;
  estimatedTokenCount: number | null;
  estimatedWordCount: number | null;
  requiredCredits: number;
  tier: SubscriptionTier;
  allowed: boolean;
  reason: "INSUFFICIENT_CREDITS" | "TIER_LOCKED" | null;
}

export interface StoryImportPassSummary {
  confidence: StoryImportConfidence;
  count: number;
  label: string;
  passNumber: number;
}

export interface StoryImportSummaryCount {
  count: number;
  elementType: StoryImportReviewElementType;
}

export interface StoryImportSourceEvidence {
  chapterLabel: string | null;
  chapterNumbers: number[];
  contradictionFlag: boolean;
  excerpt: string;
  explicitness: "explicit" | "inferred";
  paragraphLabel: string | null;
  reason: string;
  sourceRef: string | null;
  temporalMarker: string | null;
}

export interface StoryImportDocumentMetadata {
  author: string | null;
  estimatedWordCount: number;
  title: string | null;
}

export interface StoryImportSummary {
  completedAt: string | null;
  detectedFormat: StoryImportFormat;
  detectedLanguage: string | null;
  elementCounts: StoryImportSummaryCount[];
  estimatedWordCount: number;
  jobId: string;
  missingElementTypes: StoryImportReviewElementType[];
  passResults: StoryImportPassSummary[];
  projectId: string | null;
  sourceFileName: string;
}

export interface StoryImportJob {
  completedAt: string | null;
  creditsUsed: number;
  currentPass: number;
  currentStageKey?: string | null;
  detectedLanguage: string | null;
  errorText: string | null;
  filePath: string | null;
  jobId: string;
  passLabel: string | null;
  payloadPath: string | null;
  projectId: string | null;
  retryCount: number;
  sourceFileName: string | null;
  status: StoryImportJobStatus;
  summaryPath: string | null;
  totalPasses: number;
}

export interface StoryImportChapterSplit {
  bodyText: string;
  chapterNumber: number;
  endMarker: string | null;
  endOffset: number;
  sortOrder: number;
  startMarker: string;
  startOffset: number;
  title: string;
}

export interface StoryImportRelationshipSuggestion {
  fromName: string;
  label: string;
  reason: string;
  toName: string;
}

export interface StoryImportReviewElement {
  acceptedAt: string | null;
  acceptedRecordId: string | null;
  confidence: StoryImportConfidence;
  confidenceDetails: StoryImportConfidenceDetails | null;
  confidenceScore: number | null;
  discardedAt: string | null;
  displayTitle: string;
  editorTarget: StoryImportEditorTarget;
  elementData: Record<string, unknown>;
  elementType: StoryImportReviewElementType;
  evidence: StoryImportSourceEvidence[];
  id: string;
  needsReview: boolean;
  notes: string[];
  previewText: string;
  proposalTypeLabel: string | null;
  reviewStatus: StoryImportReviewStatus;
}

export interface StoryImportRequiredResolution {
  isProjectUnlocked: boolean;
  pendingChapterCount: number;
  pendingStorySpineCount: number;
}

export interface StoryImportReviewBundle {
  chapters: StoryImportChapterSplit[];
  createdAt: string;
  detectedLanguage: string;
  documentMetadata: StoryImportDocumentMetadata;
  elements: StoryImportReviewElement[];
  extractedTextHash: string;
  governanceDocId: string;
  jobId: string;
  projectId: string;
  relationshipSuggestions: StoryImportRelationshipSuggestion[];
  requiredResolution: StoryImportRequiredResolution;
  sourceFileName: string;
  storyId: string;
  storySpineId: string;
  updatedAt: string;
}

export interface StoryImportReviewGroup {
  bulkAcceptElementType: StoryImportBulkAcceptElementType | null;
  count: number;
  descriptionKey: string;
  elementType: StoryImportReviewElementType;
  emptyMessageKey: string;
  titleKey: string;
  items: StoryImportReviewElement[];
  lowConfidenceCount: number;
  needsReviewCount: number;
  pendingCount: number;
}

export interface StoryImportReviewActionResult {
  elementResults: Array<{
    elementId: string;
    recordId: string | null;
    reviewStatus: StoryImportReviewStatus;
  }>;
  jobId: string;
  projectId: string;
  requiredResolution: StoryImportRequiredResolution;
}

export interface StoryImportNotice {
  kind: "error" | "success";
  message: string;
}

export type StoryImportValidationResult =
  | {
      ok: true;
      format: StoryImportFormat;
    }
  | {
      ok: false;
      code: "IMPORT_FILE_INVALID" | "IMPORT_FILE_TOO_LARGE";
    };

const formatMimeTypes: Record<StoryImportFormat, string[]> = {
  docx: [
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ],
  md: ["text/markdown", "text/x-markdown"],
  pdf: ["application/pdf"],
  txt: ["text/plain"],
};

export function isStoryImportFormat(value: string): value is StoryImportFormat {
  return storyImportFormats.includes(value as StoryImportFormat);
}

export function isStoryImportReviewElementType(
  value: string,
): value is StoryImportReviewElementType {
  return storyImportReviewElementTypes.includes(value as StoryImportReviewElementType);
}

export function inferStoryImportFormat(
  fileName: string,
  mimeType?: string | null,
): StoryImportFormat | null {
  const normalizedExtension = fileName.split(".").pop()?.trim().toLowerCase() ?? "";

  if (isStoryImportFormat(normalizedExtension)) {
    return normalizedExtension;
  }

  const normalizedMimeType = mimeType?.trim().toLowerCase() ?? "";
  return (
    storyImportFormats.find((format) => formatMimeTypes[format].includes(normalizedMimeType))
    ?? null
  );
}

export function validateStoryImportFile(
  input: {
    fileName: string;
    fileSizeBytes: number;
    mimeType?: string | null;
  },
  fileSizeLimitBytes: number,
): StoryImportValidationResult {
  if (input.fileSizeBytes > fileSizeLimitBytes) {
    return {
      ok: false,
      code: "IMPORT_FILE_TOO_LARGE",
    };
  }

  const format = inferStoryImportFormat(input.fileName, input.mimeType);
  if (!format) {
    return {
      ok: false,
      code: "IMPORT_FILE_INVALID",
    };
  }

  return {
    ok: true,
    format,
  };
}

export function formatFileSizeLabel(bytes: number) {
  if (bytes < 1024) {
    return `${bytes} B`;
  }

  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
